import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { InvestmentModel, InvestmentsDepositsModel } from './investment.model';
import { environment } from '../../../environments/environment';
import { AmortisationModel } from './amortisation/amortisation.model';
import { InvestmentListModel } from './investmentList.model';
import { ResultModel } from '../../shared/result.model';
import { ResultTotalModel } from '../../shared/total.model';
import { ApiResponse } from '../../models/base';
import { unwrapV2, V2ApiResponse } from '../../modules_v2/shared/utils/v2-api-response.util';

const LIST_ENDPOINT = '/investments';
const V2_ASSET_ENDPOINT = '/api/v2/investment-assets';

interface ApiMessageResponse {
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class InvestmentService {
  investmentList: InvestmentModel[] = [];

  constructor(private http: HttpClient) {}

  GetAmortisation(uid: string): Promise<AmortisationModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<AmortisationModel[]>>(environment.apiUrl + V2_ASSET_ENDPOINT + `/${uid}/amortization`)),
      'No se pudo cargar el cuadro de amortización'
    );
  }

  pdfMail(uid: string): Promise<string> {
    return firstValueFrom(
      this.http.get<ApiMessageResponse>(environment.apiUrl + '/pdfmail' + `/${uid}`)
    ).then((response) => response.message);
  }

  exportInvestments(): Promise<Blob> {
    return firstValueFrom(
      this.http.get(environment.apiUrl + LIST_ENDPOINT + '/excel', {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  }

  export(uid: string): Promise<Blob> {
    return firstValueFrom(
      this.http.get(environment.apiUrl + '/amortisation/excel' + `/${uid}`, {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  }

  pdf(uid: string): Promise<Blob> {
    return firstValueFrom(
      this.http.get(environment.apiUrl + '/pdf' + `/${uid}`, {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/pdf' });
    });
  }

  addData(preinvoice: InvestmentModel): Promise<InvestmentModel> {
    return firstValueFrom(
      this.http.post<ApiResponse<InvestmentModel>>(environment.apiUrl + LIST_ENDPOINT, preinvoice)
    ).then((response) => response.data);
  }

  deleteData(uid: string): Promise<any> {
    return firstValueFrom(
      this.http.delete(environment.apiUrl + LIST_ENDPOINT + `/${uid}`)
    );
  }

  editData(uid: string, preinvoice: InvestmentModel): Promise<InvestmentModel> {
    return firstValueFrom(
      this.http.put<ApiResponse<InvestmentModel>>(
        environment.apiUrl + LIST_ENDPOINT + `/${uid}` + '/edit',
        preinvoice
      )
    ).then((response) => response.data);
  }

  loadData(uid: string): Promise<InvestmentModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<InvestmentModel>>(environment.apiUrl + V2_ASSET_ENDPOINT + `/${uid}`)),
      'No se pudo cargar la inversión'
    );
  }

  getList(): Promise<InvestmentListModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<InvestmentListModel[]>>(environment.apiUrl + LIST_ENDPOINT)
    ).then((response) => response.data);
  }

  getListSpecialInvestment(
    type: number,
    offset?: number,
    limit?: number
  ): Promise<ResultModel<InvestmentListModel>> {
    const params = new HttpParams();

    if (offset !== undefined) {
      // params = params.set('offset', offset.toString());
    }

    if (limit !== undefined) {
      // params = params.set('limit', limit.toString());
    }

    return firstValueFrom(
      this.http.get<ApiResponse<ResultModel<InvestmentListModel>>>(
        environment.apiUrl + LIST_ENDPOINT + `/${type}`,
        { params: params }
      )
    ).then((response) => response.data);
  }

  getTotal(type: number): Promise<ResultTotalModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<ResultTotalModel[]>>(environment.apiUrl + LIST_ENDPOINT + `/${type}/total`)
    ).then((response) => response.data);
  }

  getTaxBook(uids: string[], clientUid: string): Promise<Blob> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + LIST_ENDPOINT + '/taxBook/' + clientUid, uids, {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  }

  getBookAmortisation(uids: string[], clientUid: string): Promise<Blob> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + LIST_ENDPOINT + '/bookAmortisation', uids, {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  }

  public investmentDepositCreate(
    investmentDeposit: InvestmentsDepositsModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + '/investmentdeposit', investmentDeposit)
    ).then((response) => response.data);
  }

  public investmentDepositUpdate(
    investmentDeposit: InvestmentsDepositsModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + '/investmentdeposit', investmentDeposit)
    ).then((response) => response.data);
  }

  public investmentDepositDelete(investmentDepositId: number): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + '/investmentdeposit' + `/${investmentDepositId}`)
    ).then((response) => response.data);
  }

  public investmentDepositList(
    investmentIntangibleMaterialUid: string
  ): Promise<InvestmentsDepositsModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<InvestmentsDepositsModel[]>>(environment.apiUrl + V2_ASSET_ENDPOINT + `/${investmentIntangibleMaterialUid}/deposits`)),
      'No se pudieron cargar los pagos de la inversión'
    );
  }

  public investmentAmortizationTable(accountNumber: string): Promise<any> {
    let params = new HttpParams();
    params = params.append('accountNumber', accountNumber || '');

    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<any>>(environment.apiUrl + V2_ASSET_ENDPOINT + '/amortization-table', { params: params })),
      'No se pudo consultar la tabla de amortización'
    );
  }

  public addAccountingAmortisationInvestment(investmentUid: string): Promise<string> {
    return firstValueFrom(
      this.http.post<ApiResponse<string>>(environment.apiUrl + '/investment/add/amortisation/year', {
        investmentUid: investmentUid,
      })
    ).then((response) => response.data);
  }

  public deleteAccountingAmortisationInvestment(
    investmentUid: string
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + '/investment/delete/amortisation/year', {
        investmentUid: investmentUid,
      })
    ).then((response) => response.data);
  }
}